import { type ComponentLibrary } from '@suntex.classic/adobe-animate';
import { DisplayObject, MovieClip, Shape } from '@suntex.classic/createjs';
import { GraphicsUtil, UIUtil } from '@suntex.core/animation';
import { $Color, type Color } from '@suntex.core/color';
import { loggedContext } from '@suntex.core/logging';

import { ImageIcon } from './ImageIcon';
import { Label } from './Label';
import { TextIcon } from './TextIcon';
import { TextLabel } from './TextLabel';
import { BUTTON_RADIUS } from './const';

const SHADOW_BLUR = 2.0;
const SHADOW_ALPHA = 0.2;
const SHADOW_OFFSET = -2;
const SHADE_INTENSITY = 0.15;
const SHINE_INTENSITY = 0.75;
const EASE = 'easeOut';
const HOVER_SCALE = 1.1;
const CENTRAL_HOVER_SCALE = 1.05;

const DISABLED_SCALE = 0.75;
const DISABLED_ALPHA = 0.5;

const FOCUS_INTENSITY = 0.25;

const OUT_OF_FOCUS_SCALE = 0.8;
const OUT_OF_FOCUS_HOVER_SCALE = 0.82;
const OUT_OF_FOCUS_LABEL_ALPHA = 0.5;
const OUT_OF_FOCUS_ICON_ALPHA = 0.75;

const EMPHASIS_INTENSITY = 0.2;
const EMPHASIZED_SCALE = 1.2;
const EMPHASIZED_HOVER_SCALE = HOVER_SCALE * EMPHASIZED_SCALE;

export type ButtonIcon = ImageIcon | TextIcon;

@loggedContext({ autoLog: true })
export class Button extends createjs.Container {
  private flatCircle!: DisplayObject;
  private gradientCircle!: Shape;
  private shine!: DisplayObject;
  private originalScale: number;
  private label: Label | TextLabel | null = null;
  private icon: ButtonIcon;
  private buttonMc!: DisplayObject & { back: DisplayObject };
  private starMc: MovieClip | null = null;
  private enabled: boolean = true;
  private inFocus: boolean = true;
  private _alwaysShowLabels: boolean = false;
  private _isEmphasized: boolean = false;
  private baseColor: Color;
  private rendered: boolean = false;
  private _targetScale: number | null = null;

  set scale(value: number) {
    this.scaleX = value;
    this.scaleY = value;
  }

  get scale(): number {
    return this.scaleX;
  }

  get back(): DisplayObject {
    return this.buttonMc.back;
  }

  set flatten(flattenGraphics: boolean) {
    if (flattenGraphics) {
      this.flatCircle.visible = true;
      this.gradientCircle.visible = false;
      this.shine.visible = false;
    } else {
      this.flatCircle.visible = false;
      this.gradientCircle.visible = true;
      this.shine.visible = true;
    }
  }

  set alwaysShowLabels(value: boolean) {
    this._alwaysShowLabels = value;
    if (this.label) {
      this.label.alpha = value ? 1.0 : 0.0;
    }
  }

  get alwaysShowLabels(): boolean {
    return this._alwaysShowLabels;
  }

  /**
   * The button's resting scale while in a "child" position, derived from its
   * enabled/focus/emphasis state. This is the single source of truth for that
   * formula; positions with their own resting scale (center, breadcrumb) are
   * computed by `Radial` instead.
   */
  get restingChildScale(): number {
    const stateFactor = !this.enabled
      ? DISABLED_SCALE
      : !this.inFocus
        ? OUT_OF_FOCUS_SCALE
        : 1;
    return (
      this.originalScale *
      stateFactor *
      (this._isEmphasized ? EMPHASIZED_SCALE : 1)
    );
  }

  constructor(
    readonly lib: ComponentLibrary,
    radius: number,
    color: Color,
    icon: ButtonIcon,
  ) {
    super();

    //delete if indeed not needed: let bg = this.buttonMc.buttonBg;

    this.scale = radius / BUTTON_RADIUS;
    this.originalScale = this.scale;
    this.scaleX = this.scale;
    this.scaleY = this.scale;

    this.icon = icon;
    this.baseColor = color;
    this.render();
  }

  private render(): void {
    // Remove existing visual elements if they exist
    if (this.rendered) {
      this.removeChild(this.flatCircle);
      this.removeChild(this.gradientCircle);
      this.removeChild(this.buttonMc);
      this.removeChild(this.shine);
      this.removeChild(this.icon);
    } else {
      this.rendered = true;
    }

    // Determine render color based on current state
    let renderColor = this.baseColor;
    if (this._isEmphasized) {
      renderColor = $Color.getGradientColor(
        renderColor,
        'shadow',
        EMPHASIS_INTENSITY,
      );
    }
    if (!this.inFocus) {
      renderColor = $Color.lighten(renderColor, FOCUS_INTENSITY);
    }

    const shadowColor = $Color.getGradientColor(
      renderColor,
      'shadow',
      SHADE_INTENSITY,
    );

    // Create visual elements
    this.flatCircle = GraphicsUtil.createSprite((g) => {
      g.beginFill($Color.toHexString(renderColor));
      g.drawCircle(0, 0, BUTTON_RADIUS);
    });
    this.addChild(this.flatCircle);

    this.gradientCircle = GraphicsUtil.createSprite((g) => {
      GraphicsUtil.drawButtonBackground(
        g,
        BUTTON_RADIUS,
        renderColor,
        SHADE_INTENSITY,
      );
    });

    GraphicsUtil.addShadow(this.gradientCircle, {
      alpha: SHADOW_ALPHA,
      blur: SHADOW_BLUR,
    });
    this.addChild(this.gradientCircle);

    this.buttonMc = UIUtil.createDisplayObject<
      MovieClip & {
        back: DisplayObject;
      }
    >(this.lib, 'Button');
    this.addChild(this.buttonMc);
    this.buttonMc.back.visible = false;

    this.shine = GraphicsUtil.createSprite((g) => {
      GraphicsUtil.drawButtonShine(
        g,
        BUTTON_RADIUS,
        renderColor,
        this._isEmphasized ? 1 : SHINE_INTENSITY,
      );
    });
    this.addChild(this.shine);

    this.addChild(this.icon);
    this.icon.shadow = new createjs.Shadow(
      this.inFocus ? $Color.toRGBA(shadowColor, SHADOW_ALPHA) : 'transparent',
      SHADOW_OFFSET,
      SHADOW_OFFSET,
      SHADOW_BLUR,
    );

    // Apply visibility and alpha based on enabled state
    if (!this.enabled) {
      this.icon.visible = false;
      if (this.label) {
        this.label.visible = false;
      }
      if (this.starMc) {
        this.starMc.visible = false;
      }
      this.flatCircle.alpha = DISABLED_ALPHA;
      this.gradientCircle.alpha = DISABLED_ALPHA;
      this.shine.alpha = DISABLED_ALPHA;
      this.gradientCircle.shadow.blur = 0;
      this.gradientCircle.shadow.color = 'transparent';
    } else {
      this.icon.visible = true;
      this.icon.alpha = this.inFocus ? 1.0 : OUT_OF_FOCUS_ICON_ALPHA;
      if (this.label) {
        this.label.visible = true;
      }
      if (this.starMc) {
        this.starMc.visible = true;
      }
      this.flatCircle.alpha = 1.0;
      this.gradientCircle.alpha = 1.0;
      this.shine.alpha = 1.0;
      this.gradientCircle.shadow.blur = SHADOW_BLUR;
    }
  }

  public setIcon(icon: ButtonIcon): void {
    //TestUtil.log("settingIcon");
    this.removeChild(this.icon);
    this.icon = icon;
    icon.mouseEnabled = false;
    this.addChild(icon);
  }

  public setLabel(label: Label | TextLabel): void {
    this.label = label;
    this.label.alpha = 0.0;
    this.addChildAt(this.label, 0);
  }

  public star(i = 1): void {
    this.unstar();

    if (this.enabled) {
      this.starMc = UIUtil.createDisplayObject<MovieClip>(
        this.lib,
        'ProgressStar',
      );
      this.addChild(this.starMc);
      UIUtil.setFrame(this.starMc, i);
    }
  }

  public unstar() {
    if (this.starMc != null) {
      this.removeChild(this.starMc);
      this.starMc = null;
    }
  }

  public setVisualState(enabled: boolean, inFocus: boolean): void {
    this.enabled = enabled;
    this.inFocus = inFocus;
    this.render();

    // Apply label visibility and opacity
    if (this.label) {
      this.label.visible = enabled;
      this.label.alpha = enabled && !inFocus ? OUT_OF_FOCUS_LABEL_ALPHA : 1.0;
    }
  }

  public setEnabled(enabled: boolean): void {
    this.enabled = enabled;

    this.icon.visible = enabled;
    if (enabled) {
      this.icon.alpha = this.inFocus ? 1.0 : OUT_OF_FOCUS_ICON_ALPHA;
    }

    if (this.label) {
      this.label.visible = enabled;
    }
    if (this.starMc) {
      this.starMc.visible = enabled;
    }
    this.flatCircle.alpha = enabled ? 1.0 : DISABLED_ALPHA;
    this.gradientCircle.alpha = enabled ? 1.0 : DISABLED_ALPHA;
    this.shine.alpha = enabled ? 1.0 : DISABLED_ALPHA;

    if (!enabled) {
      this.gradientCircle.shadow.blur = 0;
      this.gradientCircle.shadow.color = 'transparent';
    }
  }

  public setEmphasized(emphasized: boolean): void {
    this._isEmphasized = emphasized;
    this.render();
  }

  public setScale(scale: number, animationMs: number): void {
    this._targetScale = scale;
    UIUtil.tween(this, 'scaleX', scale, animationMs, 'easeInOut');
    UIUtil.tween(this, 'scaleY', scale, animationMs, 'easeInOut');
  }

  /**
   * Immediately apply the target scale if one was set via setScale().
   * Used to finalize scale after animation interruption.
   */
  public applyTargetScale(): void {
    if (this._targetScale !== null) {
      this.scale = this._targetScale;
    }
  }

  public resetScale(animationMs: number): void {
    const scale = this.restingChildScale;
    UIUtil.tween(this, 'scaleX', scale, animationMs, 'easeInOut');
    UIUtil.tween(this, 'scaleY', scale, animationMs, 'easeInOut');
  }

  public onHover({
    animationMs,
    isCentral,
    inFocus = true,
  }: {
    animationMs: number;
    isCentral: boolean;
    inFocus?: boolean;
  }): void {
    const scale = isCentral
      ? CENTRAL_HOVER_SCALE
      : this.originalScale *
        (this._isEmphasized
          ? EMPHASIZED_HOVER_SCALE
          : inFocus
            ? HOVER_SCALE
            : OUT_OF_FOCUS_HOVER_SCALE);
    UIUtil.tween(this, 'scaleX', scale, animationMs, EASE);
    UIUtil.tween(this, 'scaleY', scale, animationMs, EASE);

    if (isCentral) {
      this.back.visible = true;
    } else {
      this.label?.onHover({ animationMs: 0 });
    }
  }

  public onLeave({
    animationMs,
    isCentral,
  }: {
    animationMs: number;
    isCentral: boolean;
  }): void {
    const scale = isCentral ? 1.0 : this.restingChildScale;
    UIUtil.tween(this, 'scaleX', scale, animationMs, EASE);
    UIUtil.tween(this, 'scaleY', scale, animationMs, EASE);

    this.label?.onLeave({
      animationMs: 0,
      alwaysShow: this.alwaysShowLabels,
    });

    this.back.visible = false;
  }
}
